import { getAccessMode } from "./accessMode";
import { applyDashboardInfoUiConfig } from "./dashboardInfoProvider";
import type {
  DashboardInfoResponse,
  DashboardInfoTab,
  DashboardInfoButton,
  DashboardInfoSidebar
} from "./dashboardInfoProvider";
import { toActionKey, canonicalizeKey } from "./keys";
import { evaluateRbac } from "./rbacEngine";
import {
  getRenderStrategy,
  resetRenderStrategyForTests
} from "./renderStrategy";
import type { ResolvedRenderStrategy } from "./renderStrategy";
import {
  evaluateUiConfig,
  isPageCoveredByUiConfig,
  setUiConfigPermissions
} from "./uiConfigEngine";
import { AccessMode, Source } from "./types";
import type { AccessQuery, PermissionMap, RenderStrategy } from "./types";

export { AccessMode, Source };
export type {
  AccessQuery,
  PermissionMap,
  RenderStrategy,
  DashboardInfoResponse,
  DashboardInfoTab,
  DashboardInfoButton,
  DashboardInfoSidebar,
  ResolvedRenderStrategy
};
export {
  getAccessMode,
  getRenderStrategy,
  resetRenderStrategyForTests,
  setUiConfigPermissions,
  isPageCoveredByUiConfig,
  applyDashboardInfoUiConfig,
  toActionKey,
  canonicalizeKey
};

/**
 * The single decision point for "can the user see/do X?".
 *
 * Consumers (Guard, useHasAccess, PrivateComponent, route loaders, selectors) call
 * `accessControl.can()` and never branch on RBAC vs UI-config themselves.
 * Cutting a tenant over is a change of accessMode, not a change of call sites.
 */
export const accessControl = {
  /**
   * @param query        what is being asked (page / entities / actions)
   * @param permissions  the RBAC permission map (userState.permissions) —
   *                     ignored for ui-config tenants
   * @param rbacOverride legacy escape hatch for PrivateComponent's custom
   *                     `checkPermission` prop; consulted only in rbac mode
   */
  can(
    query: AccessQuery,
    permissions: PermissionMap | null | undefined,
    rbacOverride?: () => boolean
  ): boolean {
    if (getAccessMode() === AccessMode.UiConfig) {
      return evaluateUiConfig(query);
    }
    if (rbacOverride) return rbacOverride();
    return evaluateRbac(query, permissions);
  },

  /** Page-level gate — used by route loaders and navigation. */
  canAccessPage(
    page: string,
    permissions: PermissionMap | null | undefined
  ): boolean {
    return this.can({ page }, permissions);
  },

  getAccessMode
};
